import React, { useRef } from 'react';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useSuperAdminDefinition } from './hooks/useSuperAdminDefinition';
import SuperAdminTable from './components/SuperAdminTable';
import SuperAdminDialog from './components/SuperAdminDialog';

const SuperAdminDefinition = () => {
    const { th } = useLanguage();
    const toast = useRef(null);

    const {
        items,
        availableUsers,
        formData,
        setFormData,
        displayModal,
        isLoading,
        isLoadingUsers,
        isAdding,
        isRemoving,
        pageNumber,
        pageSize,
        totalRecords,
        validateForm,
        handleNew,
        handleAdd: originalHandleAdd,
        handleRemove: originalHandleRemove,
        handlePageChange,
        closeModal,
        error
    } = useSuperAdminDefinition();

    const handleAdd = async () => {
        if (!validateForm()) {
            toast.current?.show({
                severity: 'warn',
                summary: th('common.warning'),
                detail: th('superAdminDefinition.validation.selectUser'),
                life: 3000
            });
            return;
        }

        try {
            await originalHandleAdd();
            toast.current?.show({
                severity: 'success',
                summary: th('common.success'),
                detail: th('superAdminDefinition.toastAddSuccess'),
                life: 3000
            });
        } catch (err) {
            toast.current?.show({
                severity: 'error',
                summary: th('common.error'),
                detail: err?.message || th('superAdminDefinition.toastError'),
                life: 3000
            });
        }
    };

    const handleRemove = (item) => {
        confirmDialog({
            header: th('superAdminDefinition.removeConfirmHeader'),
            message: th('superAdminDefinition.removeConfirm', { name: item.fullName || item.email || '-' }),
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            acceptLabel: th('common.delete'),
            rejectLabel: th('common.cancel'),
            accept: async () => {
                try {
                    await originalHandleRemove(item);
                    toast.current?.show({
                        severity: 'success',
                        summary: th('common.success'),
                        detail: th('superAdminDefinition.toastDeleteSuccess'),
                        life: 3000
                    });
                } catch (err) {
                    toast.current?.show({
                        severity: 'error',
                        summary: th('common.error'),
                        detail: err?.message || th('superAdminDefinition.toastDeleteError'),
                        life: 3000
                    });
                }
            }
        });
    };

    React.useEffect(() => {
        if (error) {
            toast.current?.show({
                severity: 'error',
                summary: th('superAdminDefinition.toastApiErrorHeader'),
                detail: th('superAdminDefinition.toastApiErrorDetail'),
                life: 3000
            });
        }
    }, [error, th]);

    return (
        <div className="card">
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0">{th('superAdminDefinition.title')}</h1>
            </div>

            <SuperAdminTable
                items={items}
                loading={isLoading}
                onNew={handleNew}
                onRemove={handleRemove}
                onPageChange={handlePageChange}
                totalRecords={totalRecords}
                pageNumber={pageNumber}
                pageSize={pageSize}
                isRemoving={isRemoving}
            />

            <SuperAdminDialog
                visible={displayModal}
                onHide={closeModal}
                formData={formData}
                setFormData={setFormData}
                onSave={handleAdd}
                userList={availableUsers}
                isLoadingUsers={isLoadingUsers}
                isAdding={isAdding}
            />
        </div>
    );
};

export default SuperAdminDefinition;
